import { ArrowSquareOutIcon, PresentationIcon, UsersThreeIcon, WarningIcon } from '@phosphor-icons/react';
import { Link } from 'react-router';
import { AppPreview } from '~/features/apps/app-preview';
import { AppDownloadMenu } from '~/features/apps/app-download-menu';
import { CanvasOpenButton } from '~/features/canvas/canvas-open-button';
import { isPlainLeftClick, useCanvasOpenHandler } from '~/features/canvas/canvas-link';

export interface DeckOutput {
  ok?: boolean;
  error?: string;
  deck?: {
    slug?: string;
    title?: string;
    slideCount?: number;
    path?: string;
    url?: string;
    updated?: boolean;
    visibility?: string;
    workspaceShared?: boolean;
    /** FRONT-2611: who can open it, computed by the tool from the row
     *  (named-people shares included). Absent on outputs saved before it. */
    audience?: string;
    updatedAt?: string;
  };
}

/**
 * The address the deck opens at. The tool output's `path` is only trusted when
 * it is a path on this site; anything else (`javascript:`, another host, a
 * protocol-relative `//` or `/\\`) falls back to the deck's own `/apps/` path,
 * so neither the new tab nor the link can be pointed somewhere else.
 */
export function deckOpenPath(path: string | undefined, slug: string): string {
  if (path && /^\/(?![\/\\])/.test(path)) return path;
  return `/apps/${encodeURIComponent(slug)}/`;
}

/**
 * FRONT-1602: the chat card for a `deck_publish` result. Same shape as the
 * sketch card (thumbnail + one-line state + link) because the user's mental
 * model is the same — "Eddy made me a thing, here is its link, ask for
 * changes and the link stays" — but the thumbnail is the live App preview,
 * since a deck IS an app and its fonts only load on the Apps origin.
 *
 * The sharing line reads the row's real state off the tool output, never a
 * remembered choice.
 */
export function DeckCard({ output }: { output: DeckOutput }) {
  const deck = output.deck;
  const slug = deck?.slug;
  // FRONT-2744: a plain click opens the Canvas when it holds this deck; else
  // today's new tab. Called before the early return (Rules of Hooks).
  const openInCanvas = useCanvasOpenHandler(`deck:${slug ?? ''}`);

  if (output.error || !slug) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2 border border-border rounded-lg">
        <WarningIcon size={14} />
        <span>{output.error ?? 'Deck failed.'}</span>
      </div>
    );
  }

  const path = deckOpenPath(deck?.path, slug);
  const count = deck?.slideCount;
  const sharing = deckSharingLine(deck);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      <div className="px-3 py-2 flex items-center gap-2 text-xs border-b border-border">
        <PresentationIcon size={14} className="text-muted-foreground shrink-0" />
        <span className="font-medium truncate text-foreground">{deck?.title ?? 'Slide deck'}</span>
        {typeof count === 'number' && (
          <span className="text-muted-foreground tabular-nums shrink-0">
            {count} {count === 1 ? 'slide' : 'slides'}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-[length:var(--text-2xs)] uppercase tracking-wide rounded-full bg-muted text-muted-foreground border border-border px-1.5 py-0.5">
          {deck?.updated ? 'Updated' : 'Deck'}
        </span>
        {/* FRONT-2024: same control the Sketches chat card uses
            (`AppDownloadMenu`, generalized from `SketchDownloadMenu`) —
            keeps the card compact, shares the trigger's own `icon-xs`
            size, no extra chrome. */}
        <AppDownloadMenu
          path={path}
          title={deck?.title ?? 'Slide deck'}
          fallbackWord="deck"
          testIdPrefix={`chat-deck-download-${slug}`}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        />
        {/* FRONT-2594: renders nothing outside a Canvas context. */}
        <CanvasOpenButton itemKey={`deck:${slug}`} />
      </div>
      <div className="p-3 flex gap-3 items-start">
        <AppPreview
          slug={slug}
          source="live"
          width={260}
          height={146}
          virtualWidth={1280}
          title={deck?.title ?? slug}
          cacheKey={deck?.updatedAt ?? null}
          label={openInCanvas ? `Open the deck ${deck?.title ?? slug} beside the chat` : undefined}
          onOpen={(e) => {
            // A box, not a link, so the browser gives it no modifier-click
            // behaviour: a plain left click opens the Canvas, a Ctrl/Cmd/Shift
            // click keeps the new tab (a middle click fires no click here).
            if (openInCanvas && isPlainLeftClick(e)) {
              openInCanvas(e);
              return;
            }
            window.open(path, '_blank', 'noopener,noreferrer');
          }}
        />
        <div className="flex-1 min-w-0 text-xs">
          <p className="mb-1.5 text-muted-foreground">
            {deck?.updated
              ? 'Updated in place. Same link, new slides.'
              : openInCanvas
                ? 'Click the preview to open it beside the chat. Arrow keys or swipe to move, F for fullscreen, N for notes.'
                : 'Open it in a new tab to present. Arrow keys or swipe to move, F for fullscreen, N for notes.'}
          </p>
          <a
            href={path}
            target="_blank"
            rel="noreferrer noopener"
            onClick={openInCanvas}
            className="inline-flex items-center gap-1 py-1.5 -my-1.5 font-medium text-foreground hover:text-primary hover:underline"
            aria-label={
              openInCanvas
                ? `Open the deck ${deck?.title ?? slug} beside the chat`
                : `Open the deck ${deck?.title ?? slug} in a new tab`
            }
          >
            Open deck
            {!openInCanvas && <ArrowSquareOutIcon size={12} className="shrink-0" />}
          </a>
          <p className="mt-2 inline-flex items-center gap-1 text-muted-foreground">
            <UsersThreeIcon size={12} className="shrink-0" />
            {sharing}
            <span aria-hidden="true">·</span>
            <Link to="/files?tab=slides" className="hover:text-foreground hover:underline">
              All slides
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * FRONT-2611: who can open the deck, in the audience words Apps and
 * Sketches already use (Only you / Shared / Public). The old line read
 * "Private: anyone in your workspace with the link can open it", which
 * contradicted itself and was false: since FRONT-856 a private, unshared
 * deck opens for its owner only. The tool's `audience` is the answer; an
 * old tool output in chat history has none, so it falls back to the raw
 * flags, which cannot see a named-people share and read it "Only you".
 */
export function deckSharingLine(deck: DeckOutput['deck']): string {
  const audience = deck?.audience ?? legacyDeckAudience(deck);
  if (audience === 'public') return 'Public: anyone with the link can open it.';
  if (audience === 'shared') {
    return deck?.workspaceShared === true
      ? 'Shared: everyone in your workspace can open it.'
      : 'Shared: the colleagues you named can open it.';
  }
  return 'Only you can open it.';
}

/** The audience of an output saved before the tool reported one. */
function legacyDeckAudience(deck: DeckOutput['deck']): string {
  if (deck?.visibility === 'public') return 'public';
  if (deck?.workspaceShared === true) return 'shared';
  return 'only-you';
}
